import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./ArtifactPaywall.module.scss"

/**
 * Artifact — an ONBOARDING paywall (paywall type randomly rolled this run —
 * rolled *Onboarding*). Built to the active Slack standing directive ("build
 * paywalls centered around loss aversion, use fear as the driving factor.
 * Include some amazing animations, highlight our feature list. Target new
 * users"): the fear is the honest fragility of a *single digital copy* — phones
 * die, storage corrupts, cards fail — and the relief is the vault sealing a
 * second, verified, full-quality copy.
 *
 * The mechanic is a **living, corrupting photograph**. Your first real memory is
 * held large on a plate. On load it is briefly pristine, then digital decay
 * honestly creeps in: the RGB channels split apart (chromatic aberration), the
 * frame tears into displaced datamosh slices, scanlines crawl and the image
 * jitters — an unprotected file rotting on a single chip. A copies counter reads
 * *1 · this device*, and the four feature guards dim to a warning amber. Nothing
 * is ever deleted: the photo is never destroyed, only shown to be *fragile*.
 * Press the CTA and a **restore wave** sweeps the plate top-to-bottom — the
 * channels snap back into register, the tears heal, the image resolves crisp and
 * full-quality, a lock stamps, the counter flips to *2 · verified & private*,
 * and every guard lights cyan.
 *
 * Loss aversion, kept honest: the peril is the *real world* — a dead chip, a
 * dropped or stolen phone — never the app taking or corrupting anything. Copy is
 * explicit that photos are always yours and never deleted; the vault only keeps a
 * second private copy. No countdown, no fake scarcity, honest savings from the
 * real debug prices.
 *
 * Divergence from the set: signature is a channel-split / datamosh corruption
 * that heals — not Lifeline's braided cord, not any scrub / flip / tide / dial.
 * Register is *dread of digital rot → relief of a verified original*. First
 * fear-led ONBOARDING paywall in the set (white space per the ledger), and the
 * first to use glitch/corruption as its visual language. Yearly is the featured,
 * most-prominent price; monthly is one demoted line. Self-contained on the
 * built-in debug product data + bundled nimbus memories; no native injection.
 */

type Tier = "premium" | "ultimate"

// The one real memory the plate holds, plus the satellites that flicker at the
// edges — all from the bundled nimbus set.
const HERO = "mem4.jpg"
const SATELLITES = ["mem7.jpg", "mem11.jpg", "mem2.jpg", "mem14.jpg"]

interface Guard {
  label: string
  detail: string
  icon: IconName
}
const GUARDS: Guard[] = [
  {
    label: "Private cloud backup",
    detail: "A second copy, kept safely off your phone",
    icon: "cloud",
  },
  {
    label: "Full-quality originals",
    detail: "Every pixel, never recompressed",
    icon: "image",
  },
  {
    label: "On every device",
    detail: "In sync wherever you sign in",
    icon: "devices",
  },
  {
    label: "Break-in alerts",
    detail: "Snaps anyone who tries a wrong PIN",
    icon: "alert",
  },
]

type IconName =
  | "lock"
  | "cloud"
  | "image"
  | "devices"
  | "alert"
  | "arrow"
  | "close"
  | "check"
  | "shield"
  | "spark"
  | "warn"

const clamp01 = (v: number) => Math.max(0, Math.min(1, v))
const lerp = (a: number, b: number, t: number) => a + (b - a) * t
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)
const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

const Icon = ({ name }: { name: IconName }) => {
  switch (name) {
    case "lock":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect
            x="4.5"
            y="10.4"
            width="15"
            height="10.2"
            rx="3"
            fill="currentColor"
          />
          <path
            d="M8 10.4V7.6a4 4 0 0 1 8 0v2.8"
            stroke="currentColor"
            strokeWidth="2.1"
            fill="none"
            strokeLinecap="round"
          />
        </svg>
      )
    case "cloud":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M7 18h10a4 4 0 0 0 .5-7.97A5.5 5.5 0 0 0 6.5 9.2 3.9 3.9 0 0 0 7 18Z"
            fill="currentColor"
          />
          <path
            d="M12 15.5v-5m0 0 2 2m-2-2-2 2"
            stroke="var(--af-ink)"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case "image":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect
            x="3.5"
            y="5"
            width="17"
            height="14"
            rx="3"
            fill="currentColor"
          />
          <circle cx="9" cy="10" r="1.7" fill="var(--af-ink)" />
          <path
            d="m5 17 4-4 3.2 3 2.3-2.3L19 17.5"
            stroke="var(--af-ink)"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case "devices":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect
            x="2.5"
            y="5.5"
            width="13"
            height="10"
            rx="2"
            fill="currentColor"
          />
          <rect
            x="16"
            y="8.5"
            width="5.5"
            height="10"
            rx="1.6"
            fill="currentColor"
          />
          <path
            d="M6 19h6"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      )
    case "alert":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 3.6 20.5 19a1 1 0 0 1-.87 1.5H4.37A1 1 0 0 1 3.5 19L12 3.6Z"
            fill="currentColor"
          />
          <path
            d="M12 9.5v4.2"
            stroke="var(--af-ink)"
            strokeWidth="1.9"
            strokeLinecap="round"
          />
          <circle cx="12" cy="16.7" r="1.1" fill="var(--af-ink)" />
        </svg>
      )
    case "shield":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 2.6 20 5.4v6c0 4.7-3.2 8.4-8 10-4.8-1.6-8-5.3-8-10v-6L12 2.6Z"
            fill="currentColor"
          />
        </svg>
      )
    case "warn":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="9.2" fill="currentColor" />
          <path
            d="M12 7.4v5.2"
            stroke="var(--af-warn-ink)"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle cx="12" cy="16.2" r="1.15" fill="var(--af-warn-ink)" />
        </svg>
      )
    case "arrow":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M5 12h13m0 0-5-5m5 5-5 5"
            stroke="currentColor"
            strokeWidth="2.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case "close":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="m6 6 12 12M18 6 6 18"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      )
    case "check":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="m5 12.5 4.5 4.5L19 7"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case "spark":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 2.5c.6 4.7 2.3 6.4 7 7-4.7.6-6.4 2.3-7 7-.6-4.7-2.3-6.4-7-7 4.7-.6 6.4-2.3 7-7Z"
            fill="currentColor"
          />
        </svg>
      )
  }
}

// A few horizontal datamosh slices. Each has a vertical band [top,bottom] of the
// plate it displaces, a horizontal shove and a stutter phase — driven by
// `corrupt` so they collapse to zero as the file heals.
const SLICES = [
  { top: 14, h: 7, shove: 26, phase: 0.0 },
  { top: 33, h: 5, shove: -34, phase: 1.7 },
  { top: 52, h: 9, shove: 18, phase: 3.1 },
  { top: 70, h: 4, shove: -22, phase: 4.6 },
  { top: 82, h: 6, shove: 30, phase: 2.3 },
]

const ArtifactPaywall = () => {
  const { state, actions } = useAppState()

  const ultimate = state.productData?.ultimate
  const premium = state.productData?.premium
  const locale =
    premium?.yearlyProduct?.priceLocale ?? ultimate?.yearlyProduct?.priceLocale

  const money = (v: number | undefined, fallback: string) =>
    v != null ? formatCurrency(v, locale) : fallback

  const plans = useMemo(
    () => ({
      premium: {
        yearly: premium?.yearlyProduct?.price ?? 35.99,
        monthly: premium?.monthlyProduct?.price ?? 13.99,
        product: premium?.yearlyProduct,
        monthlyProduct: premium?.monthlyProduct,
      },
      ultimate: {
        yearly: ultimate?.yearlyProduct?.price ?? 119.99,
        monthly: ultimate?.monthlyProduct?.price ?? 19.99,
        product: ultimate?.yearlyProduct,
        monthlyProduct: ultimate?.monthlyProduct,
      },
    }),
    [premium, ultimate],
  )

  const [tier, setTier] = useState<Tier>("premium")
  const [billing, setBilling] = useState<"yearly" | "monthly">("yearly")
  // bumps each time the file is sealed, to (re)fire the one-shot restore wave
  const [waveKey, setWaveKey] = useState(0)

  // `corrupt` is the single source of truth: 1 = file rotting, 0 = sealed clean.
  const [corrupt, setCorrupt] = useState(0)
  const corruptRef = useRef(0)
  const [sealed, setSealed] = useState(false)
  const rafRef = useRef(0)
  // continuous clock for the jitter / scanline crawl of a live-corrupting file
  const [clock, setClock] = useState(0)
  const clockRef = useRef(0)
  const clockRafRef = useRef(0)
  const joltRef = useRef(0)
  const [jolt, setJolt] = useState(0)
  const reducedMotion = useRef(false)

  const writeCorrupt = (v: number) => {
    corruptRef.current = v
    setCorrupt(v)
  }

  const animateCorruptTo = useCallback((target: number, dur = 1000) => {
    cancelAnimationFrame(rafRef.current)
    if (reducedMotion.current) {
      writeCorrupt(target)
      setSealed(target < 0.5)
      return
    }
    const from = corruptRef.current
    const start = performance.now()
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / dur)
      writeCorrupt(lerp(from, target, easeInOutCubic(t)))
      setSealed(corruptRef.current < 0.5)
      if (t < 1) rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
  }, [])

  // Tint the OS top inset to the dark instrument ground; drop the footer inset so
  // the page gradient + legal row run edge to edge.
  useEffect(() => {
    actions.setHeaderInsetColor("#0b0a12")
    actions.setIgnoreFooterInset(true)
  }, [actions])

  // Continuous clock for jitter (kept light: only advances a scalar and relaxes
  // any jolt back toward rest).
  useEffect(() => {
    reducedMotion.current =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true

    const start = performance.now()
    const tick = (now: number) => {
      clockRef.current = (now - start) / 1000
      setClock(clockRef.current)
      if (joltRef.current > 0.001) {
        joltRef.current *= 0.9
        setJolt(joltRef.current)
      }
      clockRafRef.current = requestAnimationFrame(tick)
    }
    if (!reducedMotion.current)
      clockRafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(clockRafRef.current)
  }, [])

  // Auto-demo on load: hold the pristine frame a beat, then let digital decay
  // honestly creep in — the fragile, single-copy state the CTA seals away.
  useEffect(() => {
    if (reducedMotion.current) {
      writeCorrupt(0.9)
      setSealed(false)
      return
    }
    writeCorrupt(0)
    setSealed(true)
    const t1 = setTimeout(() => {
      setSealed(false)
      // hold shy of total ruin — the memory stays visible, so the stakes read
      animateCorruptTo(0.9, 1400)
    }, 820)
    return () => {
      clearTimeout(t1)
      cancelAnimationFrame(rafRef.current)
    }
  }, [animateCorruptTo])

  // Press the plate — a visceral reminder of how little holds an unbacked file.
  const onPlatePress = useCallback(() => {
    if (corruptRef.current < 0.5) return
    joltRef.current = 1
    setJolt(1)
  }, [])

  const active = plans[tier]
  const yearlyPrice = money(
    active.yearly,
    tier === "premium" ? "$35.99" : "$119.99",
  )
  const perMonthOfYear = money(
    active.yearly / 12,
    tier === "premium" ? "$2.99" : "$9.99",
  )
  const monthlyPrice = money(
    active.monthly,
    tier === "premium" ? "$13.99" : "$19.99",
  )
  const savePercent = Math.max(
    0,
    Math.round((1 - active.yearly / (active.monthly * 12)) * 100),
  )

  const selectedProduct =
    billing === "yearly" ? active.product : active.monthlyProduct

  const buy = useCallback(() => {
    // Seal the file — restore wave heals the frame — then hand off to purchase.
    animateCorruptTo(0, 900)
    setWaveKey((k) => k + 1)
    if (selectedProduct) native.subscribe(selectedProduct)
  }, [animateCorruptTo, selectedProduct])

  const close = useCallback(() => native.postNativeEvent("close"), [])

  // ---- Derived visuals -----------------------------------------------------
  const c = clamp01(corrupt + jolt * 0.4)
  const t = clock
  // chromatic split amount (px) and jittery per-frame nudges
  const split = c * 5
  const jx = Math.sin(t * 13 + 1) * c * 2.2 + Math.sin(t * 41) * c * 0.9
  const jy = Math.cos(t * 9) * c * 1.4
  const flicker =
    0.82 + Math.sin(t * 30) * 0.06 * c + Math.sin(t * 77) * 0.04 * c

  const heroSrc = `${process.env.PUBLIC_URL}/nimbus/${HERO}`

  return (
    <div
      className={styles.page}
      data-sealed={sealed}
      style={
        {
          "--c": c,
          "--split": `${split}px`,
          "--jx": `${jx}px`,
          "--jy": `${jy}px`,
          "--flicker": flicker,
          "--heal": easeOutCubic(1 - c),
        } as React.CSSProperties
      }
    >
      {/* channel-isolation filters for the RGB split — self-contained, no assets */}
      <svg className={styles.defs} aria-hidden="true">
        <filter id="afR" colorInterpolationFilters="sRGB">
          <feColorMatrix
            type="matrix"
            values="1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0"
          />
        </filter>
        <filter id="afB" colorInterpolationFilters="sRGB">
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0"
          />
        </filter>
        <filter id="afG" colorInterpolationFilters="sRGB">
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0  0 1 0 0 0  0 0 0 0 0  0 0 0 1 0"
          />
        </filter>
      </svg>

      <div className={styles.glow} aria-hidden="true" />
      <div className={styles.grain} aria-hidden="true" />

      <button className={styles.close} onClick={close} aria-label="Not now">
        <Icon name="close" />
      </button>

      <header className={styles.intro}>
        <span className={styles.kicker}>
          <Icon name="lock" />
          Private Photo Vault
        </span>
        <h1 className={styles.headline}>
          Every photo you love is one
          <br />
          <span className={styles.hlBad}>dead chip</span> from gone.
        </h1>
        <p className={styles.sub}>
          Right now these live only on this phone. Storage fails, phones get
          lost &mdash; and a memory kept in one place has no spare. Your vault
          keeps a second, full-quality copy: verified, private, always yours.
        </p>
      </header>

      <div className={styles.statusRow}>
        <span className={styles.status} data-sealed={sealed}>
          <span className={styles.statusDot} aria-hidden="true" />
          {sealed
            ? "2 copies · verified & private"
            : "1 copy · this device only"}
        </span>
      </div>

      {/* The signature: a real memory that corrupts on a single chip, then heals
          into a sealed, verified original. */}
      <section className={styles.stage}>
        <button
          className={styles.plate}
          onPointerDown={onPlatePress}
          aria-label={
            sealed
              ? "Your memory is sealed and backed up"
              : "Tap to feel how fragile an unbacked file is"
          }
        >
          <div className={styles.frame}>
            {/* red + blue ghosts split away from the base as the file rots */}
            <img
              className={`${styles.layer} ${styles.chanR}`}
              src={heroSrc}
              alt=""
              draggable={false}
            />
            <img
              className={`${styles.layer} ${styles.chanB}`}
              src={heroSrc}
              alt=""
              draggable={false}
            />
            <img
              className={`${styles.layer} ${styles.base}`}
              src={heroSrc}
              alt="Your memory"
              draggable={false}
            />

            {/* datamosh slices — displaced bands of the same frame */}
            <div className={styles.slices} aria-hidden="true">
              {SLICES.map((s, i) => {
                const stutter = Math.sin(t * 7 + s.phase) > 0.4 ? 1 : 0.25
                const dx = s.shove * c * stutter
                return (
                  <span
                    key={i}
                    className={styles.slice}
                    style={
                      {
                        top: `${s.top}%`,
                        height: `${s.h}%`,
                        backgroundImage: `url(${heroSrc})`,
                        backgroundPositionY: `-${s.top}%`,
                        transform: `translateX(${dx}px)`,
                        opacity: c > 0.05 ? 0.9 : 0,
                      } as React.CSSProperties
                    }
                  />
                )
              })}
            </div>

            <div className={styles.scan} aria-hidden="true" />
            <div className={styles.blocks} aria-hidden="true" />

            {/* restore wave — sweeps top→bottom each time the file is sealed */}
            <div
              key={waveKey}
              className={styles.restore}
              data-run={waveKey > 0}
              aria-hidden="true"
            />

            {/* corner readout: copies + integrity of THIS file */}
            <span className={styles.copies} data-sealed={sealed}>
              <Icon name={sealed ? "check" : "warn"} />
              {sealed ? "Backed up" : "Unprotected"}
            </span>

            {/* the seal that stamps when the frame is clean */}
            <span className={styles.seal} data-on={sealed} aria-hidden="true">
              <Icon name="lock" />
            </span>
          </div>

          {/* satellites — the rest of the roll, each flickering at the edge */}
          <div className={styles.satellites} aria-hidden="true">
            {SATELLITES.map((f, i) => (
              <span className={styles.sat} data-i={i} key={f}>
                <img
                  src={`${process.env.PUBLIC_URL}/nimbus/${f}`}
                  alt=""
                  draggable={false}
                />
              </span>
            ))}
          </div>
        </button>

        {/* what sealing turns on — the real protections, highlighted */}
        <ul className={styles.guards}>
          {GUARDS.map((g) => (
            <li className={styles.guard} key={g.label} data-on={sealed}>
              <span className={styles.guardIcon}>
                <Icon name={g.icon} />
              </span>
              <span className={styles.guardText}>
                <strong>{g.label}</strong>
                <span>{g.detail}</span>
              </span>
              <span className={styles.guardMark} aria-hidden="true">
                <Icon name={sealed ? "check" : "warn"} />
              </span>
            </li>
          ))}
        </ul>
      </section>

      {/* reassurance — the spine of App-Store compliance, said plainly */}
      <p className={styles.reassure}>
        <Icon name="shield" />
        Your photos are always yours &mdash; nothing is ever deleted. The vault
        simply keeps a second private copy, so a bad chip or a lost phone never
        means a lost memory.
      </p>

      {/* pricing — yearly featured; monthly demoted to a single line */}
      <section className={styles.pricing}>
        <div
          className={styles.tierSwitch}
          role="tablist"
          aria-label="Choose a plan"
        >
          <button
            role="tab"
            aria-selected={tier === "premium"}
            className={`${styles.tierBtn} ${tier === "premium" ? styles.tierOn : ""}`}
            onClick={() => setTier("premium")}
          >
            Premium
          </button>
          <button
            role="tab"
            aria-selected={tier === "ultimate"}
            className={`${styles.tierBtn} ${styles.tierUlt} ${
              tier === "ultimate" ? styles.tierOn : ""
            }`}
            onClick={() => setTier("ultimate")}
          >
            <Icon name="spark" />
            Ultimate &middot; 1 TB
          </button>
        </div>

        <button
          className={`${styles.yearCard} ${billing === "yearly" ? styles.yearOn : ""}`}
          onClick={() => setBilling("yearly")}
          aria-pressed={billing === "yearly"}
        >
          <span className={styles.yearBadge}>
            BEST VALUE &middot; SAVE {savePercent}%
          </span>
          <span className={styles.yearMain}>
            <span className={styles.yearName}>
              {tier === "premium" ? "Premium" : "Ultimate"} &middot; Yearly
            </span>
            <span className={styles.yearPrice}>{yearlyPrice}</span>
          </span>
          <span className={styles.yearSub}>
            <span>{perMonthOfYear}/mo, billed yearly</span>
            <span>7-day free trial</span>
          </span>
          <span className={styles.radio} aria-hidden="true" />
        </button>

        <button
          className={`${styles.monthLine} ${
            billing === "monthly" ? styles.monthOn : ""
          }`}
          onClick={() => setBilling("monthly")}
          aria-pressed={billing === "monthly"}
        >
          Prefer monthly? <strong>{monthlyPrice}/mo</strong>
        </button>

        <button className={styles.cta} onClick={buy}>
          <span>Seal my memories in the vault</span>
          <Icon name="arrow" />
        </button>

        <p className={styles.fine}>
          {billing === "yearly"
            ? `7-day free trial, then ${yearlyPrice} per year`
            : `${monthlyPrice} per month`}
          . Auto-renews until canceled &middot; cancel anytime.
        </p>

        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span aria-hidden="true">&middot;</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span aria-hidden="true">&middot;</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </section>
    </div>
  )
}

export default ArtifactPaywall
